/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { ThemeContext } from '@cvent/carina-theme-provider';
import Magazine from '@cvent/carina-magazine';
import { ChevronDownIcon } from '@cvent/carina-icon';
import getSelectStyle from './SelectStyle';
import getConditionalStyle from './SelectStyleLogic';
import { injectTestId } from '@cvent/nucleus-test-automation';

export default class Combobox extends React.Component {
  static propTypes = {
    /** options is an array of options* that become the individual dropdown items
     *
     *
     * *options can take one of a few shapes: the 'default' shape for them is an object with a value (any type)
     * and a label: (what actually gets displayed inside of the drop down), which should be either a string or a
     * render-able function/node, plus optional heading an disabled props.
     * The value prop is the only one that is required in this form, if no label is provided,
     * then the value will determine it
     * Alternatively, an option can be just a string, and not an object,
     * in which case the string will determine the other properties
     Options are user selectable, filterable choices, displayed in the menu in the order provided.
     * value: Primary identifier of an option. Each 'value' must be unique: Options with duplicate values will be removed! 'value' is the only property searched with the default filter.
     * label: 'label' value is rendered when 'option' is selected, and also in the menu. If no 'label' is provided, the 'value' will be copied to 'label'. A React element can also be provided for custom rendering.
     * disabled: Display disabled styling for 'option', remove interactivity (cannot be selected).
     * payload: Optional property for storing additional data (usually used upon selection).
     */
    options: PropTypes.arrayOf(
      PropTypes.shape({
        /** Each 'value' must be unique: Options with duplicate values will be removed! 'value' is the only property searched with the default filter.
         */
        value: PropTypes.string.isRequired,
        /** 'label' value is rendered when 'option' is selected, and also in the menu. If no 'label' is provided, the 'value' will be copied to 'label'. A render-props component can also be provided for custom rendering, 'theme' will be passed back as an argument.
         */
        label: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.func,
          PropTypes.element
        ]),
        /** If heading evaluates to true, option will be styled as a heading, and will not be interactive */
        heading: PropTypes.bool,
        /** Display disabled styling for 'option', remove interactivity (cannot be selected). */
        disabled: PropTypes.bool,
        /** Adds styling to options that actuate destructive actions */
        destructive: PropTypes.bool,
        /** adds line break at top of option */
        lineBreak: PropTypes.bool,
        /** Optional property for storing additional data (usually used upon selection). */
        payload: PropTypes.any
      })
    ).isRequired,

    /**
     * Callback is triggered when user makes a valid selection from options.
     * `selection.value` should usually be passed back to the 'selected' prop, via state.
     */
    onUpdate: PropTypes.func.isRequired,

    /** Function to apply to each 'option'. Return 'true' to display 'option' in menu.
     * If the filter prop is left undefined, the default filter will match the input string to the 'value' property of the option, regardless of case, or position. If matching to 'label' is needed, provide a custom 'filter' function prop.
     *
     * To return the list of options without filtering (for instance, if server-side filtering is being applied), provide a function that always returns a truthy value.
     */
    filter: PropTypes.func,

    /** Use 'selected' to set the option to be displayed. 'selected' refers to the value of Combobox's selection. The state of Combobox's selection must be managed. The most common use of 'selected' is to set from state via the 'onUpdate' function. */
    selected: PropTypes.string,

    /** placeholder message to display when no user input or selection is made.*/
    placeholder: PropTypes.string,

    /** String or element to display in menu when no options pass the 'filter' function */
    noMatchMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),

    /** Display disabled styling, remove interactivity. */
    disabled: PropTypes.bool,

    /** Display elements right-to-left (for use with RLT languages). If Combobox is used with Carina 'FormElement,' 'isRtl' value will likely be inherited */
    isRtl: PropTypes.bool,

    /** Display error styling. */
    hasError: PropTypes.bool,

    /** HTML id attribute will be applied as appropriate */
    id: PropTypes.string,

    /** For e2e tests (maps to `data-cvent-id`) */
    testID: PropTypes.string,

    /** aria-label or accessibilityLabel attribute */
    accessibilityLabel: PropTypes.string,

    /** Reference a validation message element via the aria-describedby attribute */
    describedBy: PropTypes.string,

    /** Text to display on 'Clear Selection' option in menu */
    clearSelectionText: PropTypes.string,

    /** For Testing Only: open Magazine */
    _initiallyOpen: PropTypes.bool
  };

  static defaultProps = {
    clearSelectionText: 'Clear Selection',
    noMatchMessage: 'No Results Found'
  };

  static contextType = ThemeContext;

  inputRef = React.createRef();
  magazineRef = React.createRef();

  state = {
    isOpen: this.props._initiallyOpen || false,
    inputValue: ''
  };

  render() {
    let inputIsFocused;
    if (typeof document !== 'undefined') {
      inputIsFocused = document.activeElement === this.inputRef.current;
    }

    /* deconstruct props for propDrop */
    const { isOpen, inputValue } = this.state;
    const {
      clearSelectionText,
      disabled,
      hasError,
      id,
      isRtl,
      onUpdate,
      options,
      required,
      selected,
      testID,
      noMatchMessage,
      _initiallyOpen,
      placeholder,
      ...propDrop
    } = this.props;

    const showPlaceholder = !inputValue && !selected;
    const magazineId = id ? `${id}-magazine` : undefined;
    const magazineTestID = testID ? `${testID}-magazine` : undefined;

    /* build style */
    const themedStyle = getSelectStyle(this.context);
    const {
      inputContainer: inputContainerStyle,
      input: inputStyle,
      iconContainer: iconContainerStyle,
      iconColor,
      magazineContainer: magazineStyleObject,
      clearSelection: clearSelectionStyle,
      noMatchMessage: noMatchMessageStyle
    } = getConditionalStyle(themedStyle, {
      isOpen,
      isRtl,
      disabled,
      hasError,
      inputIsFocused,
      showPlaceholder
    });

    return (
      <React.Fragment>
        <div
          {...injectTestId(testID)}
          {...(id ? { id: `${id}-outer` } : {})}
          css={inputContainerStyle}
          onClick={this.handleSelectClick}
        >
          {!isOpen && !inputIsFocused && (
            <this.TextDisplay
              inputStyle={inputStyle}
              placeHolder={placeholder}
            />
          )}
          <this.TextInput
            required={required}
            inputStyle={inputStyle}
            magazineId={magazineId}
            propDrop={propDrop}
            id={id}
          />
          <span css={iconContainerStyle}>
            <ChevronDownIcon color={iconColor.color} />
          </span>
          {this.state.isOpen && (
            <Magazine
              id={magazineId}
              testID={magazineTestID}
              options={
                this.getFilteredOptions(
                  clearSelectionStyle,
                  noMatchMessageStyle
                ) || []
              }
              theme={this.context}
              handleClose={this.closeDropdown}
              onUpdate={this.handleUpdate}
              selected={selected}
              magazineStyleObject={magazineStyleObject}
              isRtl={this.props.isRtl}
              hasError={this.props.hasError}
              forwardedRef={this.magazineRef}
              refToFocusOnKeyboardInput={this.inputRef}
              keyPressCallback={this.addKeyToInput}
            />
          )}
        </div>
      </React.Fragment>
    );
  }

  TextInput = ({ inputStyle, magazineId, required, propDrop, id }) => {
    const { inputValue, isOpen } = this.state;
    const { disabled, selected, placeholder } = this.props;
    const { accessibilityLabel, ...restProps } = propDrop;
    return (
      <input
        aria-label={accessibilityLabel || selected || placeholder}
        aria-invalid={false}
        {...restProps}
        tabIndex={disabled ? -1 : 0}
        {...(id ? { id } : {})}
        required={required}
        type="text"
        css={inputStyle}
        value={inputValue}
        disabled={disabled}
        onChange={this.handleInputChange}
        onFocus={this.handleInputFocusOrBlur}
        onBlur={this.handleInputFocusOrBlur}
        onKeyDown={this.handleInputKeyPress}
        ref={this.inputRef}
        role="combobox" // eslint-disable-line
        {...(this.state.isOpen ? { 'aria-controls': magazineId } : {})}
        aria-expanded={isOpen}
        aria-live="polite"
      />
    );
  };
  TextDisplay = ({ inputStyle }) => {
    const { disabled, selected, placeholder } = this.props;
    return (
      <div
        css={{ ...inputStyle, pointerEvents: 'none' }}
        disabled={disabled}
        onChange={this.handleInputChange}
        onFocus={this.handleInputFocusOrBlur}
        onBlur={this.handleInputFocusOrBlur}
        onKeyDown={this.handleInputKeyPress}
        aria-hidden
      >
        {(selected && this.selectedLabel(selected)) || placeholder}
      </div>
    );
  };

  selectedLabel = () => {
    const { selected: selectedValue, options } = this.props;
    const currentOption = (options || []).filter(
      ({ value }) => value === selectedValue
    );
    if (currentOption[0]) {
      const { label, value } = currentOption[0];
      const labelType = typeof label;
      if (labelType === 'string' || labelType === 'number') {
        return label;
      }
      return value;
    }
    return selectedValue;
  };

  handleUpdate = option => {
    if (typeof this.props.onUpdate === 'function') {
      this.props.onUpdate(option.value, option);
    }

    this.setState({ isOpen: false, inputValue: '' });
  };

  // prevents lingering focus state when tabbing does not cause re-render
  handleInputFocusOrBlur = () => this.forceUpdate();

  getFilteredOptions = (clearSelectionStyle, noMatchMessageStyle) => {
    const { inputValue } = this.state;
    const { options, filter } = this.props;
    if (filter && typeof filter === 'function') {
      return options.filter(option => filter(option, inputValue));
    }
    return this.defaultOptionsFilter(
      options,
      inputValue,
      clearSelectionStyle,
      noMatchMessageStyle
    );
  };

  defaultOptionsFilter = (
    options,
    inputValue,
    clearSelectionStyle,
    noMatchMessageStyle
  ) => {
    if (inputValue.trim().length > 0) {
      const { matchStart, matchMiddle } = options.reduce(
        (accumulator, option) => {
          const { label, value } = option;
          const stringForMatch = (
            (typeof label === 'string' && label) ||
            (typeof value === 'string' && value) ||
            (typeof value === 'number' && `${value}`) ||
            ''
          ).toLowerCase();
          const inputStringForComparison = inputValue.toLowerCase().trim();
          if (stringForMatch.includes(inputStringForComparison)) {
            if (stringForMatch.startsWith(inputStringForComparison)) {
              return {
                matchStart: [...accumulator.matchStart, option],
                matchMiddle: accumulator.matchMiddle
              };
            }
            return {
              matchStart: accumulator.matchStart,
              matchMiddle: [...accumulator.matchMiddle, option]
            };
          }
          return accumulator;
        },
        { matchStart: [], matchMiddle: [] }
      );

      if (matchStart.length === 0 && matchMiddle.length === 0) {
        return [
          {
            value: '',
            disabled: true,
            label: () => (
              <span css={noMatchMessageStyle}>{this.props.noMatchMessage}</span>
            )
          }
        ];
      }
      // add linebreak between match groups
      if (matchMiddle.length > 0 && matchStart.length > 0) {
        matchMiddle[0] = { ...matchMiddle[0], lineBreak: true };
      }
      return [...matchStart, ...matchMiddle];
    } else if (this.props.selected) {
      // add "Clear Selection" option
      return [
        {
          value: '',
          label: () => (
            <span css={clearSelectionStyle}>
              {this.props.clearSelectionText}
            </span>
          )
        },
        ...options
      ];
    }
    return options;
  };

  handleSelectClick = () => {
    if (!this.props.disabled) {
      this.setState(
        { isOpen: true },
        () => this.inputRef.current && this.inputRef.current.focus()
      );
    }
  };

  handleInputChange = event => {
    if (typeof this.props.onChange === 'function') this.props.onChange(event);
    this.setState({ inputValue: event.target.value, isOpen: true });
  };

  componentDidMount() {
    if (typeof document !== 'undefined') {
      document.addEventListener('click', this.onClickOutside, false);
    }
  }

  componentWillUnmount() {
    if (typeof document !== 'undefined') {
      document.removeEventListener('click', this.onClickOutside, false);
    }
  }

  onClickOutside = event => {
    // check if clicked node is part of this component
    if (!ReactDOM.findDOMNode(this).contains(event.target)) {
      this.setState({ isOpen: false, inputValue: '' });
    }
  };

  handleInputKeyPress = event => {
    if (
      event.key === 'ArrowDown' ||
      event.key === 'ArrowUp' ||
      event.key === 'Enter'
    ) {
      event.preventDefault();
      this.setState(
        { isOpen: true },
        () => this.magazineRef.current && this.magazineRef.current.focus()
      );
    } else if (event.key === 'Tab') {
      this.setState({ isOpen: false, inputValue: '' });
    }
  };

  closeDropdown = () => {
    this.setState({ isOpen: false, inputValue: '' });
  };

  handleSelect = option => {
    this.props.onUpdate && this.props.onUpdate(option && option.value, option); // eslint-disable-line no-unused-expressions
    this.closeDropdown();
  };

  addKeyToInput = key =>
    this.setState({ inputValue: this.state.inputValue + key });
}
